import React, { useEffect, useContext } from 'react';
import { AsSelect } from '@adserve/adserve-react-components';
import JourneyBar from './JourneyBar/JourneyBar';
import { AirtimePlanningContext } from '../../contexts/AirtimePlanningContext';
import { campaignBriefInputState } from '../Campaign/CampaignBrief/CampaignBriefUtils';
import { stationSelectionInputState } from '../Campaign/CampaignStations/CampaignStationsUtils';
import { automationSettingsInputState } from '../Campaign/AutomationSettings/AutomationSettingsUtils';
import { airtimePlanningInputState } from '../Campaign/AirtimePlanning/AirtimePlanningUtils';
import { propertySelectionInputState } from '../Campaign/Properties/PropertySelectionUtils';
import { offAirCostsInputState } from '../Campaign/OffAirCosts/OffAirCostsUtils';
import { bespokeBillingInputState } from '../Campaign/BespokeBilling/BespokeBillingUtils';
import { BusinessTypes } from '../../constants/Enums';
import { quoteAndBookingInputState } from '../Campaign/QuoteAndBooking/QuoteAndBookingUtils';
import { transmissionTimesInputState } from '../Campaign/TransmissionTimes/TransmissionTimesUtils';
import { campaignBurstBriefInputState } from '../Campaign/CampaignBurstBrief/CampaignBurstBriefUtils';
import TopBarCtas from './TopBarCtas';

const TopBar = ({
    title,
    currentStep,
    setCurrentStep,
    handleClose,
    handleUpdateWithMessage,
    createCampaign,
    handleQualifyOutClick,
    handleCancelCampaignClick,
    handleAmendCampaignClick,
    handleDiscardCampaignClick,
    campaign,
    journeyBarLoader,
    setJourneyBarLoader,
    readOnly,
    bookedReadOnly,
    onEdit,
    showEditButton,
    campaignVersions,
    currentCampaignVersionId = window.emptyGuid,
    setCurrentCampaignVersionId,
    activeCancellationRequest,
    validCampaignName,
    validBurstName,
    validCampaignGoal,
    validCategory,
    validDeal,
    validDates,
    campaignBurst,
    externalIsBooked,
    isBurst,
    campaignSaving,
    disableBackButton = false
}) => {
    // Styles
    const containerStyle = {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        padding: '10px 0px 10px 30px',
        position: 'sticky',
        top: 0,
        zIndex: 990,
        gap: 10,
        boxShadow: '3px 2px 11px 0 rgba(0,0,0,0.33)'
    };

    const titleStyle = {
        fontSize: 22,
        fontWeight: 500,
        color: '#414141'
    };

    const titleflex = {
        display: 'flex',
        alignItems: 'center'
    };

    const titleVersionStyle = {
        fontSize: 15,
        fontWeight: 500,
        color: '#8A8A8A',
        padding: '6px 2px 6px 10px'
    };

    const titleCampaignIdStyle = {
        fontSize: 14,
        fontWeight: 500,
        color: '#8A8A8A'
    };

    const { journeyBarSteps, setJourneyBarSteps } = useContext(AirtimePlanningContext);
    // State

    useEffect(() => {
        loadJourneyBarState(campaignBurst.id ? campaignBurst : campaign);
        setJourneyBarLoader(!journeyBarLoader);
    }, [
        campaign,
        campaign.id,
        campaign.offairActivities,
        campaign.currentPlan,
        campaign.currentPlan?.quoted,
        campaign.journeyBarSteps,
        campaign.hasBookedSpots,
        campaign.hasQuotes,
        campaignBurst,
        campaignBurst.id
    ]);

    const stepByName = (journeyBarSteps, name) => {
        const sa = journeyBarSteps.filter((s) => s.name === name);
        if (sa.length) {
            return sa[0];
        }
    };

    const EnableJourneyBarSteps = (steps, creating, campaign) => {
        if (creating) {
            steps?.forEach((s, i) => {
                if (i > 0) {
                    s.enabled = false;
                }
            });
        } else {
            steps?.forEach((s, i) => {
                let e = true;
                JSON.parse(s.requiredSteps)?.forEach((st, j) => {
                    if (!stepByName(steps, st)?.stepCompleted) {
                        e = false;
                    }
                });
                if (!e && JSON.parse(s.alternateRequiredSteps).length > 0) {
                    e = true;
                    JSON.parse(s.alternateRequiredSteps)?.forEach((st, j) => {
                        if (!stepByName(steps, st)?.stepCompleted) {
                            e = false;
                        }
                    });
                }

                if (s.hidden) {
                    e = false;
                }

                // Additional checks for specific steps goes here
                if (e) {
                    if (s.name === 'TransmissionTimes') {
                        if (
                            campaign.campaignStatusName === 'Draft' &&
                            campaign?.currentPlan?.reservedUntil < new Date()
                        ) {
                            e = false;
                        }
                    }
                } else {
                    if (s.name === 'TransmissionTimes') {
                        if (campaign?.businessType?.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase()) {
                            if (campaign.hasBookedSpots) {
                                e = true;
                            }
                        }
                    }
                }

                s.enabled = e;
            });
        }

        return steps;
    };

    const loadJourneyBarState = (campaign) => {
        let state;
        let stepsCopy = [...journeyBarSteps];

        stepsCopy?.forEach(async (st) => {
            switch (st.name) {
                case 'CampaignBrief':
                    state = campaignBriefInputState(campaign);
                    st.stepCompleted = state.stepCompleted;
                    st.stepEdited = state.stepEdited;
                    break;
                case 'BurstBrief':
                    state = campaignBurstBriefInputState(campaign);
                    st.stepCompleted = state.stepCompleted;
                    st.stepEdited = state.stepEdited;
                    break;
                case 'StationSelection':
                    if (campaign?.id !== window.emptyGuid) {
                        st.enabled = true;
                    }
                    state = stationSelectionInputState(campaign);
                    st.stepCompleted = state.stepCompleted;
                    st.stepEdited = state.stepEdited;
                    break;
                case 'AutomationSettings':
                    if (campaign?.id !== window.emptyGuid) {
                        st.enabled = true;
                    }
                    state = automationSettingsInputState(campaign);
                    st.stepCompleted = state.stepCompleted;
                    st.stepEdited = state.stepEdited;
                    break;
                case 'AirtimePlanning':
                    state = airtimePlanningInputState(campaign);
                    st.stepCompleted = state.stepCompleted;
                    st.stepEdited = state.stepEdited;
                    break;
                case 'PropertySelection':
                    state = propertySelectionInputState(campaign);
                    st.stepCompleted = state.stepCompleted;
                    st.stepEdited = state.stepEdited;
                    break;
                case 'OffAirCosts':
                    state = offAirCostsInputState(campaign);
                    st.stepCompleted = state.stepCompleted;
                    st.stepEdited = state.stepEdited;
                    break;
                case 'BespokeBilling':
                    state = bespokeBillingInputState(campaign);
                    st.stepCompleted = state.stepCompleted;
                    st.stepEdited = state.stepEdited;
                    st.hidden = state.hidden;

                    if (campaign?.businessType?.toLowerCase() !== BusinessTypes.SANDP.name.toLowerCase()) {
                        if (!state.hidden) {
                            st.enabled = true;
                        }
                    }

                    if (campaign?.bespokeBillingNotSaved) {
                        st.enabled = false;
                    }
                    break;
                case 'QuoteBooking':
                    state = quoteAndBookingInputState(campaign);
                    if (campaign?.id !== window.emptyGuid) {
                        st.enabled = true;
                    }
                    st.stepCompleted = state.stepCompleted;
                    st.stepEdited = state.stepEdited;
                    break;
                case 'TransmissionTimes':
                    state = transmissionTimesInputState(campaign);
                    st.stepCompleted = state.stepCompleted;
                    st.stepEdited = state.stepEdited;
                    break;
                case 'Billing':
                    if (campaign?.businessType?.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase()) {
                        if (campaign.hasBookedSpots) {
                            st.enabled = true;
                        }
                    }
                    break;
                case 'Performance':
                    if (campaign?.businessType?.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase()) {
                        if (campaign.hasBookedSpots) {
                            st.enabled = true;
                        }
                    }
                    break;
                default:
                // no checks required
            }
        });

        stepsCopy = EnableJourneyBarSteps(stepsCopy, campaign.id === window.emptyGuid, campaign);

        setJourneyBarSteps(stepsCopy);
    };

    const handleEdit = () => {
        onEdit(false);
    };

    return (
        <>
            <div style={containerStyle}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0px 3px',
                        margin: '0 30px'
                    }}
                >
                    <div>
                        <div
                            id={'campaign-name-title'}
                            style={{ ...titleStyle, ...(campaignVersions?.length > 1 ? titleflex : {}) }}
                        >
                            <span id='title'>{title}</span>
                            {campaignVersions?.length <= 1 ? (
                                <span id={'campaign-version-input'} style={titleVersionStyle}>
                                    {campaignBurst.id
                                        ? campaignBurst.versionNumber > 0
                                            ? campaignBurst.versionName
                                            : ''
                                        : campaign.versionNumber > 0
                                            ? campaign.versionName
                                            : ''}
                                </span>
                            ) : (
                                <AsSelect
                                    labelStyle={{ padding: 0 }}
                                    style={{
                                        ...titleVersionStyle,
                                        width: 130,
                                        marginLeft: 15
                                    }}
                                    values={campaignVersions}
                                    value={currentCampaignVersionId}
                                    onChange={(e) => setCurrentCampaignVersionId(e.target.value)}
                                />
                            )}
                        </div>
                        <div style={titleCampaignIdStyle}>{campaign?.referenceNumber}</div>
                    </div>
                    <TopBarCtas
                        campaign={campaignBurst.id ? campaignBurst : campaign}
                        parentCampaign={campaignBurst.id ? campaign : undefined }
                        isCampaignBurst={!!campaignBurst.id}
                        createCampaign={createCampaign}
                        currentCampaignVersionId={currentCampaignVersionId}
                        handleAmendCampaignClick={handleAmendCampaignClick}
                        handleCancelCampaignClick={handleCancelCampaignClick}
                        handleClose={handleClose}
                        handleDiscardCampaignClick={handleDiscardCampaignClick}
                        handleEdit={handleEdit}
                        handleQualifyOutClick={handleQualifyOutClick}
                        handleUpdateWithMessage={handleUpdateWithMessage}
                        activeCancellationRequest={activeCancellationRequest}
                        bookedReadOnly={bookedReadOnly}
                        readOnly={readOnly}
                        showEditButton={showEditButton}
                        validCampaignGoal={validCampaignGoal}
                        validCampaignName={validCampaignName}
                        validBurstName={validBurstName}
                        validCategory={validCategory}
                        validDeal={validDeal}
                        validDates={validDates}
                        currentStep={currentStep}
                        externalIsBooked={externalIsBooked}
                        isBurst={isBurst}
                        campaignSaving={campaignSaving}
                        disableBackButton={disableBackButton}
                    />
                </div>

                <JourneyBar currentStep={currentStep} setCurrentStep={setCurrentStep} />
            </div>
        </>
    );
};

export default TopBar;
